/**
 * O2 Multi Select Component
 *
 * Searchable multi-select built on top of `brn-command` (the same primitive used
 * by the filter popovers across the app) with a trigger styled like
 * `hlm-select-trigger`, so it is a drop-in replacement for
 * `<hlm-select multiple>` whenever the option list is long enough to need a
 * search box (users, projects, …).
 *
 * O2 CUSTOM: lives outside the spartan folders, so it is safe from library
 * updates. It is the multi-value counterpart of `o2-autocomplete`.
 *
 * Performance notes:
 * - The option list lives inside `*brnPopoverContent`, so nothing is rendered
 *   until the popover is opened.
 * - Selection lookups go through a `Set` (`_selectedSet`), so toggling stays
 *   O(1) regardless of how many options/selections there are.
 * - Filtering is delegated to `brn-command`, which only toggles a `data-hidden`
 *   attribute instead of re-creating the DOM on every keystroke.
 *
 * @example
 * ```html
 * <o2-multi-select
 *   [options]="users()"
 *   [value]="selectedIds()"
 *   (valueChange)="selectedIds.set($event)"
 *   placeholder="Seleziona utenti"
 *   searchPlaceholderText="Cerca utente"
 *   emptyText="Nessun utente trovato"
 * />
 * ```
 */
import type { BooleanInput } from "@angular/cdk/coercion";
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  linkedSignal,
  model,
  output,
  viewChild,
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { provideIcons } from "@ng-icons/core";
import { lucideChevronDown, lucideSearch } from "@ng-icons/lucide";
import { BrnCommandImports } from "@spartan-ng/brain/command";
import { BrnPopover, BrnPopoverImports } from "@spartan-ng/brain/popover";
import { HlmCheckboxImports } from "@spartan-ng/helm/checkbox";
import { HlmCommandImports } from "@spartan-ng/helm/command";
import { HlmIconImports } from "@spartan-ng/helm/icon";
import { HlmPopoverImports } from "@spartan-ng/helm/popover";
import { hlm } from "@spartan-ng/helm/utils";
import type { ClassValue } from "clsx";

/**
 * Base interface for O2 Multi Select options.
 *
 * Mirrors the `o2-autocomplete` convention: options expose an `id` (the emitted
 * value) and a `displayName` (what the user reads and searches for). Both can be
 * overridden through `optionValue` / `optionLabel` for exotic shapes.
 */
export interface O2MultiSelectOptionBase {
  /** Value emitted when the option is selected. */
  id: string;
  /** Display string shown in the list, the trigger and used for searching. */
  displayName: string;
}

const defaultOptionValue = <T extends O2MultiSelectOptionBase>(
  option: T,
): string => option.id;

const defaultOptionLabel = <T extends O2MultiSelectOptionBase>(
  option: T,
): string => option.displayName ?? "";

export const O2_MULTI_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => O2MultiSelectComponent),
  multi: true,
};

@Component({
  selector: "o2-multi-select",
  standalone: true,
  imports: [
    HlmIconImports,
    HlmCheckboxImports,
    HlmCommandImports,
    HlmPopoverImports,
    BrnCommandImports,
    BrnPopoverImports,
  ],
  providers: [
    O2_MULTI_SELECT_VALUE_ACCESSOR,
    provideIcons({ lucideChevronDown, lucideSearch }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "block",
  },
  template: `
    <hlm-popover
      align="start"
      sideOffset="5"
      closeDelay="100"
      (closed)="_onTouched()"
    >
      <button
        type="button"
        hlmPopoverTrigger
        [id]="_triggerId()"
        [class]="_computedTriggerClass()"
        [disabled]="_disabled()"
        [attr.aria-label]="ariaLabel() || null"
      >
        <span class="truncate">{{ _displayText() }}</span>
        <ng-icon hlm size="sm" class="ml-2 flex-none" name="lucideChevronDown" />
      </button>

      <hlm-command
        *brnPopoverContent="let ctx"
        hlmPopoverContent
        class="p-0"
        [style.width.px]="_elementRef.nativeElement.offsetWidth"
      >
        <hlm-command-search>
          <ng-icon hlm name="lucideSearch" class="text-muted-foreground" />
          <input [placeholder]="searchPlaceholderText()" hlm-command-search-input />
        </hlm-command-search>

        <div *brnCommandEmpty hlmCommandEmpty>{{ emptyText() }}</div>

        <hlm-command-list [class]="_computedListClass()">
          <hlm-command-group>
            @for (option of _normalizedOptions(); track option.value) {
              <button
                hlm-command-item
                type="button"
                class="min-w-0"
                [value]="option.label"
                (selected)="_toggle(option.value)"
              >
                <hlm-checkbox
                  class="mr-2 shrink-0 pointer-events-none"
                  [checked]="_selectedSet().has(option.value)"
                />
                <span class="truncate min-w-0">{{ option.label }}</span>
              </button>
            }
          </hlm-command-group>
        </hlm-command-list>
      </hlm-command>
    </hlm-popover>
  `,
})
export class O2MultiSelectComponent<
  T extends O2MultiSelectOptionBase = O2MultiSelectOptionBase,
> implements ControlValueAccessor
{
  private static _id = 0;

  protected readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The popover wrapping the searchable list, closed when disabled. */
  private readonly _popover = viewChild(BrnPopover);

  /** The full list of selectable options. */
  public readonly options = input<readonly T[]>([]);

  /** The currently selected values. */
  public readonly value = model<string[]>([]);

  /** Extracts the emitted value from an option. Defaults to `option.id`. */
  public readonly optionValue = input<(option: T) => string>(
    defaultOptionValue,
  );

  /** Extracts the label from an option. Defaults to `option.displayName`. */
  public readonly optionLabel = input<(option: T) => string>(
    defaultOptionLabel,
  );

  /** Text shown in the trigger when nothing is selected. */
  public readonly placeholder = input<string>("Seleziona");

  /** Placeholder of the search input. */
  public readonly searchPlaceholderText = input<string>("Cerca");

  /** Text shown when the search yields no result. */
  public readonly emptyText = input<string>("Nessun risultato.");

  /**
   * How many labels are listed in the trigger before falling back to a
   * "{n} selezionati" summary.
   */
  public readonly maxDisplayedLabels = input<number>(3);

  /**
   * Builds the summary shown when more than `maxDisplayedLabels` options are
   * selected. Receives the number of selected options.
   */
  public readonly summaryWith = input<(count: number) => string>(
    (count) => `${count} selezionati`,
  );

  /**
   * Id applied to the trigger button (for `<label for>` association).
   * Falls back to a generated id when empty.
   */
  public readonly triggerId = input<string>("");

  private readonly _fallbackTriggerId = `o2-multi-select-${++O2MultiSelectComponent._id}`;

  protected readonly _triggerId = computed(
    () => this.triggerId() || this._fallbackTriggerId,
  );

  /** Aria label for the trigger button. */
  public readonly ariaLabel = input<string>("");

  /** Whether the control is disabled. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** Custom class for the trigger button. */
  public readonly triggerClass = input<ClassValue>("");

  /** Custom class for the option list container. */
  public readonly listClass = input<ClassValue>("");

  /** Emitted whenever the selection changes. */
  public readonly valueChange = output<string[]>();

  /** Disabled state, kept in sync with both the input and `setDisabledState`. */
  protected readonly _disabled = linkedSignal(() => this.disabled());

  /**
   * Options pre-projected to `{ value, label }`.
   *
   * Doing it once here keeps the template free of function calls that would run
   * on every change detection cycle for every row.
   */
  protected readonly _normalizedOptions = computed(() => {
    const toValue = this.optionValue();
    const toLabel = this.optionLabel();
    return this.options().map((option) => ({
      value: toValue(option),
      label: toLabel(option),
    }));
  });

  /** Selected values as a `Set`, for O(1) lookups while rendering the list. */
  protected readonly _selectedSet = computed(() => new Set(this.value()));

  /** Label shown in the trigger. */
  protected readonly _displayText = computed(() => {
    const selected = this._selectedSet();
    if (selected.size === 0) {
      return this.placeholder();
    }

    const labels = this._normalizedOptions()
      .filter((option) => selected.has(option.value))
      .map((option) => option.label);

    // Selected values with no matching option (e.g. a removed user) are not
    // rendered, but they still count towards the selection.
    if (labels.length === 0) {
      return this.summaryWith()(selected.size);
    }
    if (labels.length <= this.maxDisplayedLabels()) {
      return labels.join(", ");
    }
    return this.summaryWith()(labels.length);
  });

  protected readonly _computedTriggerClass = computed(() =>
    hlm(
      "border-input bg-background hover:bg-accent/30 focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center justify-between gap-2 overflow-hidden rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
      this._disabled() ? "" : "cursor-pointer",
      this.triggerClass(),
    ),
  );

  protected readonly _computedListClass = computed(() =>
    hlm("max-h-72", this.listClass()),
  );

  private _onChange?: (value: string[]) => void;
  protected _onTouched: () => void = () => {
    /* noop until registered */
  };

  /** Toggles a value in/out of the selection and propagates the change. */
  protected _toggle(optionValue: string): void {
    if (this._disabled()) return;

    const current = this.value();
    const next = this._selectedSet().has(optionValue)
      ? current.filter((value) => value !== optionValue)
      : [...current, optionValue];

    this.value.set(next);
    this._onChange?.(next);
    this.valueChange.emit(next);
  }

  /** @internal ControlValueAccessor */
  writeValue(value: string[] | null | undefined): void {
    this.value.set(Array.isArray(value) ? value : []);
  }

  /** @internal ControlValueAccessor */
  registerOnChange(fn: (value: string[]) => void): void {
    this._onChange = fn;
  }

  /** @internal ControlValueAccessor */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** @internal ControlValueAccessor */
  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
    if (isDisabled) {
      this._popover()?.close();
    }
  }
}
